import { forwardRef, useContext } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { CarouselRootContext } from './context/carousel-root.context';
import { carouselScrollContainerStyles } from './styles/carousel-scroll-container.styles';
import type { CarouselRootContextInterface } from './types/carousel-root.types';
import type { CarouselScrollContainerPropsInterface } from './types/carousel-scroll-container.types';

export const CarouselScrollContainer = forwardRef<
  HTMLUListElement,
  CarouselScrollContainerPropsInterface
>((props: CarouselScrollContainerPropsInterface, forwardedRef) => {
  const {
    className,
    css: cssProp,
    enableSnap = { base: false, lg: true },
    hasFocusableContent = true,
    scrollOffset = 'none',
    slideGap = 'sm',
    ...rest
  } = props;

  const { carousel } = useContext<CarouselRootContextInterface>(CarouselRootContext);

  const responsiveEnableSnap = splitResponsiveVariant('enableSnap', enableSnap);
  const responsiveScrollOffset = splitResponsiveVariant('scrollOffset', scrollOffset);
  const responsiveSlideGap = splitResponsiveVariant('slideGap', slideGap);

  const styles = css(
    carouselScrollContainerStyles.raw({
      ...responsiveEnableSnap,
      ...responsiveScrollOffset,
      ...responsiveSlideGap,
    }),
    cssProp,
  );

  return (
    <ul
      ref={forwardedRef}
      role="list"
      data-c11n-version={__C11N_VERSION__}
      data-c11n-component="Carousel.ScrollContainer"
      className={cx('c11n', 'c11n_carousel__scroll-container', styles, className)}
      tabIndex={hasFocusableContent ? undefined : 0}
      onScroll={carousel?.handleOnScroll}
      onKeyDown={carousel?.scrollToFocusedEl}
      {...rest}
    />
  );
});

CarouselScrollContainer.displayName = 'Carousel.ScrollContainer';
